import React, { useEffect, useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import apiClient from "../services/api";
import { EmptyState } from "../components/common/EmptyState";
import { ErrorState } from "../components/common/ErrorState";
import { StatusBadge } from "../components/common/StatusBadge";
import { formatDate } from "../utils/date-format";
import { useToast } from "../contexts/ToastContext";

interface SourceLink {
  sourceId: string;
  discoveredAt: string;
  source: {
    id: string;
    name: string;
  };
}

interface AnalysisData {
  id: string;
  status: "pending" | "completed" | "skipped" | "failed";
  isGrantRelated: boolean;
  classificationConfidence: number;
  classificationMethod: "KEYWORDS" | "LLM";
  extractionStatus: string | null;
  extractedStatus: string | null;
  extractionConfidence: number | null;
  errorMessage: string | null;
  processedAt: string | null;
}

interface Article {
  id: string;
  url: string;
  title: string;
  contentHash: string | null;
  rawContent: string | null;
  lastFetchedAt: string | null;
  createdAt: string;
  updatedAt: string;
  sourceLinks: SourceLink[];
  analysis?: AnalysisData | null;
  recommendationCount?: number;
}

interface ArticlesResponse {
  articles: Article[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface Source {
  id: string;
  name: string;
}

interface SourcesResponse {
  sources: Source[];
}

interface ContentFetchResult {
  success: boolean;
  articleId: string;
  contentLength: number;
  changed: boolean;
  error?: string;
  durationMs: number;
}

interface AnalysisTriggerResult {
  success: boolean;
  message: string;
  articleId: string;
  correlationId: string;
}

type AnalysisStatusFilter =
  | "none"
  | "pending"
  | "completed"
  | "skipped"
  | "failed";

/**
 * Polish labels for extraction status values.
 * Used for accessibility (aria-label) on extraction status badges.
 */
const EXTRACTION_STATUS_LABELS: Record<string, string> = {
  OPENED: "Nabor otwarty",
  CLOSED: "Nabor zamkniety",
  SUSPENDED: "Nabor wstrzymany",
  CANCELLED: "Nabor odwolany",
  EXTENDED: "Termin przedluzony",
  RESUMED: "Nabor wznowiony",
  UNDER_REVIEW: "Nabor rozpatrywany",
  RESULTS: "Wyniki ogloszone",
  UPDATE: "Aktualizacja informacji",
};

/**
 * Polish tooltips with detailed explanations for extraction status values.
 * Used for title attribute on extraction status badges to help users understand each status.
 */
const EXTRACTION_STATUS_TOOLTIPS: Record<string, string> = {
  OPENED: "Nabor otwarty - mozna skladac wnioski",
  CLOSED: "Nabor zamkniety - termin skladania wnioskow minal",
  SUSPENDED: "Nabor wstrzymany - czasowe zawieszenie przyjmowania wnioskow",
  CANCELLED: "Nabor odwolany - nabor zostal anulowany przed zakonczeniem",
  EXTENDED: "Termin przedluzony - wydluzono czas na skladanie wnioskow",
  RESUMED: "Nabor wznowiony - ponownie przyjmowane sa wnioski",
  UNDER_REVIEW: "Nabor rozpatrywany - trwa ocena wnioskow przed ogloszeniem wynikow",
  RESULTS: "Wyniki ogloszone - opublikowano liste beneficjentow",
  UPDATE: "Aktualizacja informacji - zmieniono szczegoly naboru",
};

/**
 * Maps analysis status to badge properties
 */
const ANALYSIS_STATUS_CONFIG: Record<
  string,
  { status: "analysis-none" | "analysis-pending" | "analysis-completed" | "analysis-skipped" | "analysis-failed"; label: string }
> = {
  none: { status: "analysis-none", label: "Nie analizowano" },
  pending: { status: "analysis-pending", label: "Analiza..." },
  completed: { status: "analysis-completed", label: "Przeanalizowano" },
  skipped: { status: "analysis-skipped", label: "Pominięty" },
  failed: { status: "analysis-failed", label: "Błąd analizy" },
};

/**
 * Get color class for confidence value.
 * Green for >0.8, yellow for 0.5-0.8, red for <0.5.
 */
const getConfidenceColorClass = (confidence: number): string => {
  if (confidence > 0.8) return "text-green-600";
  if (confidence >= 0.5) return "text-yellow-600";
  return "text-red-600";
};

/**
 * Get filter-specific empty state messages based on analysisStatus filter.
 */
const getEmptyStateForAnalysisFilter = (
  analysisStatus: AnalysisStatusFilter | undefined
): { title: string; description: string } => {
  switch (analysisStatus) {
    case "none":
      return {
        title: "Brak artykułów bez analizy",
        description: "Wszystkie artykuły zostały już przeanalizowane.",
      };
    case "pending":
      return {
        title: "Brak artykułów w trakcie analizy",
        description: "Aktualnie żadne artykuły nie są analizowane.",
      };
    case "completed":
      return {
        title: "Brak przeanalizowanych artykułów",
        description: "Uruchom analizę dla wybranych artykułów.",
      };
    case "skipped":
      return {
        title: "Brak pominiętych artykułów",
        description: "Żadne artykuły nie zostały pominięte podczas analizy.",
      };
    case "failed":
      return {
        title: "Brak artykułów z błędem analizy",
        description: "Wszystkie analizy zakończyły się pomyślnie.",
      };
    default:
      return {
        title: "Brak artykułów dla wybranych filtrów",
        description: "Zmień kryteria wyszukiwania lub usuń filtry.",
      };
  }
};

/**
 * Get visual styling based on article status.
 * Articles without content get amber styling, recently fetched get green left border.
 */
const getArticleStyle = (article: Article): string => {
  if (!article.contentHash) {
    return "border-l-4 border-amber-400 bg-amber-50";
  }
  if (article.lastFetchedAt) {
    const fetchedAt = new Date(article.lastFetchedAt);
    const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    if (fetchedAt > dayAgo) {
      return "border-l-4 border-green-400";
    }
  }
  return "";
};

/**
 * Page for viewing discovered articles from information sources.
 * Supports filtering by source and content status.
 */
const DiscoveryArticles: React.FC = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceId = searchParams.get("sourceId") || undefined;
  const hasContentParam = searchParams.get("hasContent");
  const hasContent =
    hasContentParam === "true"
      ? true
      : hasContentParam === "false"
        ? false
        : undefined;
  const analysisStatusParam = searchParams.get("analysisStatus") as AnalysisStatusFilter | null;
  const analysisStatus = analysisStatusParam || undefined;
  const page = parseInt(searchParams.get("page") || "1", 10);

  const [expandedArticleId, setExpandedArticleId] = useState<string | null>(
    null
  );
  const [expandedAnalysisId, setExpandedAnalysisId] = useState<string | null>(
    null
  );
  const [fetchingArticleId, setFetchingArticleId] = useState<string | null>(
    null
  );
  const [analyzingArticleId, setAnalyzingArticleId] = useState<string | null>(
    null
  );
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    document.title = "Odkryte artykuły - KiedyNabory.pl";
  }, []);

  // Mutation for fetching article content
  const fetchContentMutation = useMutation({
    mutationFn: async (articleId: string) => {
      setFetchingArticleId(articleId);
      const { data } = await apiClient.post<ContentFetchResult>(
        `/grants/discovery-articles/${articleId}/fetch-content`
      );
      return data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["discovery-articles"] });
      if (result.success) {
        addToast({
          message: `Treść pobrana! ${result.contentLength} znaków${result.changed ? " (zmieniono)" : ""}`,
          type: "success",
          duration: 5000,
        });
      } else {
        addToast({
          message: `Błąd: ${result.error}`,
          type: "error",
          duration: 10000,
        });
      }
    },
    onError: (error: Error) => {
      addToast({
        message: `Błąd: ${error.message}`,
        type: "error",
        duration: 10000,
      });
    },
    onSettled: () => {
      setFetchingArticleId(null);
    },
  });

  // Mutation for triggering article analysis
  const triggerAnalysisMutation = useMutation({
    mutationFn: async ({ articleId, force }: { articleId: string; force?: boolean }) => {
      setAnalyzingArticleId(articleId);
      const params = new URLSearchParams();
      if (sourceId) params.set("sourceId", sourceId);
      if (force) params.set("force", "true");
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const { data } = await apiClient.post<AnalysisTriggerResult>(
        `/grants/discovery-articles/${articleId}/analyze${queryString}`
      );
      return data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["discovery-articles"] });
      addToast({
        message: result.message,
        type: "success",
        duration: 5000,
      });
      // Start polling for status updates
      startPolling(result.articleId);
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || error.message;
      addToast({
        message: `Błąd: ${message}`,
        type: "error",
        duration: 10000,
      });
    },
    onSettled: () => {
      setAnalyzingArticleId(null);
    },
  });

  // Track active polling intervals for cleanup (useRef avoids re-render issues)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Close expanded panels on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setExpandedArticleId(null);
        setExpandedAnalysisId(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Polling for analysis status (max 60s, every 5s)
  const startPolling = (articleId: string) => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    let attempts = 0;
    const maxAttempts = 12; // 60s / 5s
    const pollInterval = setInterval(async () => {
      attempts++;
      await queryClient.invalidateQueries({ queryKey: ["discovery-articles"] });

      // Check if analysis is complete
      const updatedData = queryClient.getQueryData<ArticlesResponse>([
        "discovery-articles",
        sourceId,
        hasContent,
        analysisStatus,
        page,
      ]);
      const article = updatedData?.articles.find((a) => a.id === articleId);
      if (article?.analysis?.status !== "pending" || attempts >= maxAttempts) {
        clearInterval(pollInterval);
        pollingIntervalRef.current = null;

        // Show timeout notification if polling ended due to maxAttempts and status is still pending
        if (attempts >= maxAttempts && article?.analysis?.status === "pending") {
          addToast({
            message:
              "Analiza trwa dłużej niż zwykle. Odśwież stronę za chwilę.",
            type: "info",
            duration: 10000,
          });
        }
      }
    }, 5000);

    pollingIntervalRef.current = pollInterval;
  };

  // Fetch sources for filter dropdown
  const { data: sourcesData } = useQuery<SourcesResponse>({
    queryKey: ["grants-sources-list"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/sources");
      return data;
    },
  });

  // Fetch articles
  const { data, isLoading, isFetching, error, refetch } =
    useQuery<ArticlesResponse>({
      queryKey: ["discovery-articles", sourceId, hasContent, analysisStatus, page],
      queryFn: async () => {
        const params = new URLSearchParams();
        if (sourceId) params.set("sourceId", sourceId);
        if (hasContent !== undefined)
          params.set("hasContent", String(hasContent));
        if (analysisStatus) params.set("analysisStatus", analysisStatus);
        params.set("page", String(page));
        params.set("limit", "20");

        const { data } = await apiClient.get(
          `/grants/discovery-articles?${params.toString()}`
        );
        return data;
      },
    });

  const handleFilterChange = (key: string, value: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set("page", "1"); // Reset to page 1 on filter change
    setSearchParams(newParams);
  };

  const handlePageChange = (newPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", String(newPage));
    setSearchParams(newParams);
  };

  const selectedSource = sourcesData?.sources.find((s) => s.id === sourceId);

  const handleRetry = async () => {
    if (isRetrying) return;
    setIsRetrying(true);
    try {
      await refetch();
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">
              Odkryte artykuły
              {selectedSource && (
                <span className="text-lg font-normal text-neutral-600 ml-2">
                  - {selectedSource.name}
                </span>
              )}
            </h1>
            <p className="mt-1 text-sm text-neutral-600 text-left">
              Artykuły odkryte przez system ze skonfigurowanych źródeł informacji
            </p>
          </div>
          <Link
            to="/grants/sources"
            className="text-primary-600 hover:text-primary-800 text-sm"
          >
            &larr; Wróć do źródeł
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Źródło
              </label>
              <select
                value={sourceId || ""}
                onChange={(e) =>
                  handleFilterChange("sourceId", e.target.value || null)
                }
                disabled={!sourcesData}
                className="rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm disabled:bg-neutral-100 disabled:cursor-wait"
              >
                {!sourcesData ? (
                  <option value="">Ładowanie...</option>
                ) : (
                  <>
                    <option value="">Wszystkie źródła</option>
                    {sourcesData.sources.map((source) => (
                      <option key={source.id} value={source.id}>
                        {source.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Status treści
              </label>
              <select
                value={
                  hasContent === undefined ? "" : hasContent ? "true" : "false"
                }
                onChange={(e) =>
                  handleFilterChange("hasContent", e.target.value || null)
                }
                className="rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
              >
                <option value="">Wszystkie</option>
                <option value="true">Z treścią</option>
                <option value="false">Bez treści</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Status analizy
              </label>
              <select
                value={analysisStatus || ""}
                onChange={(e) =>
                  handleFilterChange("analysisStatus", e.target.value || null)
                }
                className="rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
              >
                <option value="">Wszystkie</option>
                <option value="none">Nie analizowano</option>
                <option value="pending">Analiza w toku</option>
                <option value="completed">Przeanalizowano</option>
                <option value="skipped">Pominięty</option>
                <option value="failed">Błąd analizy</option>
              </select>
            </div>

            {data && (
              <div className="ml-auto flex items-end">
                <span className="text-sm text-neutral-600">
                  Znaleziono: <strong>{data.total}</strong> artykułów
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Articles List */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {isLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">
                Ładowanie artykułów...
              </p>
            </div>
          ) : error ? (
            <ErrorState
              title="Blad ladowania artykulow"
              message={(error as Error).message || "Nieznany blad"}
              onRetry={handleRetry}
              retryLabel={isRetrying ? "Ładowanie..." : "Spróbuj ponownie"}
            />
          ) : (
            <>
              <div className="divide-y divide-neutral-200">
                {data?.articles.map((article) => (
                  <div
                    key={article.id}
                    data-testid={`article-row-${article.id}`}
                    className={`p-4 hover:bg-opacity-75 transition-colors ${getArticleStyle(article)}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3
                            className="text-sm font-medium text-neutral-900 truncate"
                            title={article.title}
                          >
                            {article.title}
                          </h3>
                          {!article.contentHash && (
                            <StatusBadge
                              status="new"
                              label="Nowy"
                              className="whitespace-nowrap"
                            />
                          )}
                        </div>
                        <a
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={article.url}
                          className="text-xs text-primary-600 hover:text-primary-800 truncate block max-w-md text-left"
                        >
                          {article.url}
                        </a>
                        <div className="mt-1 flex items-center gap-3 text-xs text-neutral-500">
                          <span>Odkryto: {formatDate(article.createdAt)}</span>
                          {article.lastFetchedAt && (
                            <span>
                              Pobrano: {formatDate(article.lastFetchedAt)}
                            </span>
                          )}
                          {article.sourceLinks.length > 0 && (
                            <span>
                              Źródła:{" "}
                              {article.sourceLinks
                                .map((l) => l.source.name)
                                .join(", ")}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center flex-wrap gap-2 sm:ml-4">
                        {/* Content status */}
                        {article.contentHash ? (
                          <StatusBadge status="fetched" label="Pobrano" />
                        ) : (
                          <StatusBadge status="pending" label="Oczekuje" />
                        )}

                        {/* Analysis status */}
                        {(() => {
                          const status = article.analysis?.status || "none";
                          const config = ANALYSIS_STATUS_CONFIG[status] || ANALYSIS_STATUS_CONFIG.none;
                          return (
                            <>
                              <span
                                role="status"
                                aria-live="polite"
                                className="sr-only"
                              >
                                Status analizy: {config.label}
                              </span>
                              <StatusBadge
                                status={config.status}
                                label={config.label}
                                aria-busy={status === "pending"}
                              />
                            </>
                          );
                        })()}

                        {/* Fetch content button */}
                        {!article.contentHash && (
                          <button
                            onClick={() =>
                              fetchContentMutation.mutate(article.id)
                            }
                            disabled={fetchingArticleId === article.id}
                            aria-busy={fetchingArticleId === article.id}
                            className={`text-xs ${
                              fetchingArticleId === article.id
                                ? "text-neutral-400 cursor-wait"
                                : "text-green-600 hover:text-green-800"
                            }`}
                          >
                            {fetchingArticleId === article.id
                              ? "Pobieram..."
                              : "Pobierz treść"}
                          </button>
                        )}

                        {/* Trigger analysis button - only for articles with content */}
                        {article.contentHash && (
                          <button
                            onClick={() =>
                              triggerAnalysisMutation.mutate({
                                articleId: article.id,
                                force: !!article.analysis, // force=true for re-analysis
                              })
                            }
                            disabled={
                              analyzingArticleId === article.id ||
                              article.analysis?.status === "pending"
                            }
                            aria-busy={
                              analyzingArticleId === article.id ||
                              article.analysis?.status === "pending"
                            }
                            aria-label="Analizuj ponownie"
                            title={
                              analyzingArticleId === article.id
                                ? "Uruchamianie analizy..."
                                : article.analysis?.status === "pending"
                                  ? "Analiza w toku - poczekaj na zakończenie"
                                  : undefined
                            }
                            className={`text-xs ${
                              analyzingArticleId === article.id ||
                              article.analysis?.status === "pending"
                                ? "text-neutral-400 cursor-wait"
                                : "text-amber-600 hover:text-amber-800"
                            }`}
                          >
                            {analyzingArticleId === article.id
                              ? "Uruchamiam..."
                              : article.analysis?.status === "pending"
                                ? "Analizuję..."
                                : article.analysis
                                  ? "Analizuj ponownie"
                                  : "Analizuj"}
                          </button>
                        )}

                        {/* Show content button */}
                        {article.rawContent && (
                          <button
                            onClick={() => {
                              const isExpanding = expandedArticleId !== article.id;
                              setExpandedArticleId(
                                expandedArticleId === article.id
                                  ? null
                                  : article.id
                              );
                              if (isExpanding) {
                                setTimeout(() => {
                                  document.getElementById(`article-content-${article.id}`)?.scrollIntoView({
                                    behavior: "smooth",
                                    block: "nearest",
                                  });
                                }, 100);
                              }
                            }}
                            aria-expanded={expandedArticleId === article.id}
                            aria-controls={`article-content-${article.id}`}
                            className="text-xs text-primary-600 hover:text-primary-800"
                          >
                            {expandedArticleId === article.id
                              ? "Zwiń treść"
                              : "Pokaż treść"}
                          </button>
                        )}

                        {/* Show analysis details button */}
                        {article.analysis && (
                          <button
                            onClick={() => {
                              const isExpanding = expandedAnalysisId !== article.id;
                              setExpandedAnalysisId(
                                expandedAnalysisId === article.id
                                  ? null
                                  : article.id
                              );
                              if (isExpanding) {
                                setTimeout(() => {
                                  document.getElementById(`article-analysis-${article.id}`)?.scrollIntoView({
                                    behavior: "smooth",
                                    block: "nearest",
                                  });
                                }, 100);
                              }
                            }}
                            aria-expanded={expandedAnalysisId === article.id}
                            aria-controls={`article-analysis-${article.id}`}
                            className="text-xs text-primary-700 hover:text-primary-800"
                          >
                            {expandedAnalysisId === article.id
                              ? "Zwiń analizę"
                              : "Pokaż analizę"}
                          </button>
                        )}

                        {/* Link to recommendations for this article */}
                        {article.recommendationCount != null &&
                          article.recommendationCount > 0 && (
                            <Link
                              to={`/grants/admin/recommendations?articleId=${article.id}&status=all`}
                              className="text-xs text-purple-600 hover:text-purple-800"
                            >
                              Rekomendacje ({article.recommendationCount})
                            </Link>
                          )}
                      </div>
                    </div>

                    {/* Expanded content */}
                    {expandedArticleId === article.id && article.rawContent && (
                      <div
                        id={`article-content-${article.id}`}
                        role="region"
                        aria-label={`Treść artykułu: ${article.title}`}
                        className="mt-3 p-3 bg-neutral-50 rounded text-xs text-neutral-700 max-h-64 overflow-y-auto whitespace-pre-wrap"
                      >
                        {article.rawContent.substring(0, 2000)}
                        {article.rawContent.length > 2000 && "..."}
                      </div>
                    )}

                    {/* Expanded analysis details */}
                    {expandedAnalysisId === article.id && article.analysis && (
                      <div
                        id={`article-analysis-${article.id}`}
                        role="region"
                        aria-label={`Analiza artykułu: ${article.title}`}
                        className="mt-3 p-3 bg-primary-50 rounded text-xs text-neutral-700"
                      >
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                          <div>
                            <span className="font-medium">Status:</span>{" "}
                            {article.analysis.status}
                          </div>
                          <div>
                            <span className="font-medium">Dotyczący grantu:</span>{" "}
                            {article.analysis.isGrantRelated ? "Tak" : "Nie"}
                          </div>
                          <div>
                            <span className="font-medium">Metoda:</span>{" "}
                            {article.analysis.classificationMethod}
                          </div>
                          <div>
                            <span className="font-medium">Pewność:</span>{" "}
                            <span className={getConfidenceColorClass(article.analysis.classificationConfidence)}>
                              {(article.analysis.classificationConfidence * 100).toFixed(0)}%
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Typ:</span>{" "}
                            {article.analysis.extractedStatus ? (
                              <StatusBadge
                                status={`extraction-${article.analysis.extractedStatus}` as keyof typeof import("../components/common/StatusBadge").STATUS_COLORS}
                                label={article.analysis.extractedStatus}
                                aria-label={EXTRACTION_STATUS_LABELS[article.analysis.extractedStatus] || article.analysis.extractedStatus}
                                title={EXTRACTION_STATUS_TOOLTIPS[article.analysis.extractedStatus]}
                              />
                            ) : (
                              <StatusBadge
                                status="unknown"
                                label="Nie rozpoznano"
                                aria-label="Status nierozpoznany - analiza nie okresliła typu naboru"
                                title="Status nierozpoznany - analiza nie okresliła typu naboru"
                              />
                            )}
                          </div>
                          {article.analysis.extractionConfidence && (
                            <div>
                              <span className="font-medium">Pewność ekstrakcji:</span>{" "}
                              <span className={getConfidenceColorClass(article.analysis.extractionConfidence)}>
                                {(article.analysis.extractionConfidence * 100).toFixed(0)}%
                              </span>
                            </div>
                          )}
                          {article.analysis.processedAt && (
                            <div>
                              <span className="font-medium">Przeanalizowano:</span>{" "}
                              {formatDate(article.analysis.processedAt)}
                            </div>
                          )}
                          {article.analysis.errorMessage && (
                            <div className="col-span-2 sm:col-span-4 text-red-600">
                              <span className="font-medium">Błąd:</span>{" "}
                              {article.analysis.errorMessage.length > 500
                                ? article.analysis.errorMessage.substring(0, 500) + "..."
                                : article.analysis.errorMessage}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {(!data?.articles || data.articles.length === 0) && (() => {
                const hasFilters = sourceId || hasContent !== undefined || analysisStatus;
                const emptyStateMessages = hasFilters && analysisStatus && !sourceId && hasContent === undefined
                  ? getEmptyStateForAnalysisFilter(analysisStatus)
                  : hasFilters
                    ? { title: "Brak artykułów dla wybranych filtrów", description: "Zmień kryteria wyszukiwania lub usuń filtry." }
                    : { title: "Brak artykułów", description: "Uruchom discovery dla źródeł, aby pobrać artykuły." };

                return (
                  <EmptyState
                    title={emptyStateMessages.title}
                    description={emptyStateMessages.description}
                    action={
                      hasFilters ? (
                        <button
                          onClick={() => setSearchParams(new URLSearchParams())}
                          className="mt-2 text-primary-600 hover:text-primary-800 font-medium"
                        >
                          Wyczyść filtry
                        </button>
                      ) : (
                        <Link
                          to="/grants/sources"
                          className="mt-2 text-primary-600 hover:text-primary-800 font-medium inline-block"
                        >
                          Przejdź do źródeł
                        </Link>
                      )
                    }
                    className="p-6"
                  />
                );
              })()}

              {/* Pagination */}
              {data && data.totalPages > 1 && (
                <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between">
                  <div className="text-sm text-neutral-600">
                    {isFetching ? (
                      <span className="text-neutral-500">Ładowanie...</span>
                    ) : (
                      <>
                        Strona {data.page} z {data.totalPages}
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(data.page - 1)}
                      disabled={data.page === 1 || isFetching}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50"
                    >
                      {isFetching ? "Ładowanie..." : "Poprzednia"}
                    </button>
                    <button
                      onClick={() => handlePageChange(data.page + 1)}
                      disabled={data.page >= data.totalPages || isFetching}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50"
                    >
                      {isFetching ? "Ładowanie..." : "Następna"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DiscoveryArticles;
